import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { TRACK_EVENTS } from "core/consts";
import { TrackEventFn } from "core/types";
import RSButton from "ds_legacy/components/RSButton";
import TextAreaInputField from "ds_legacy/components/TextArea/TextAreaForm";
import TextInputField from "ds_legacy/components/TextInputField";
import { withTrackingEvent } from "ds_legacy/hoc";
import { TEXT_DARK_HINT } from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, sizing } from "ds_legacy/materials/metrics";
import { Heading, Link, Title } from "ds_legacy/materials/typography";
import Image from "dsl/atoms/Image";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useModal } from "dsl/hooks";
import { customAlphabet } from "nanoid";
import React from "react";
import {
  SimpleFormRenderProps,
  convertModelDefinition,
  valueDef,
} from "react-forms-state";

type ErrorInfo = {
  componentStack: string;
};

function generateUID() {
  // for 500 IDs per hour, ~20 days are needed in order to
  // have a 1% probability of at least one collision.
  // https://zelark.github.io/nano-id-cc/
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const nanoid = customAlphabet(alphabet, 8);
  return nanoid();
}

function shouldFilterEvent(content: string): [boolean, string | null] {
  try {
    // 134k total, 700 per day
    if (content.includes("Zugriff verweigert")) return [true, "access-denied"];

    // 26k total, up to 150 per day
    if (
      content.includes(
        "In den Microsoft-Interneterweiterungen ist ein interner Fehler aufgetreten",
      )
    )
      return [true, "microsoft-internet-extensions"];

    // 5k total, up to 40 per day
    if (content.includes("Der Vorgang konnte aufgrund"))
      return [true, "unknown-process-error"];
  } catch (_) {
    return [false, null];
  }
  return [false, null];
}

export function BugReportDialog({
  open,
  setOpen,
  uid,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  uid: string;
}) {
  const toast = useToast();

  const onClose = () => setOpen(false);
  return (
    <SimpleFormRenderProps
      formInputValue={null}
      modelDefinition={convertModelDefinition({
        ...valueDef("name", { fieldRequired: true }),
        ...valueDef("email", { fieldRequired: true }),
        ...valueDef("message", { fieldRequired: true }),
      })}
      onSubmit={({
        email,
        message,
        name,
      }: {
        email: string;
        message: string;
        name: string;
      }) => {
        console.error(`[BUG REPORT][${uid}] ${name} | ${email} | ${message} `);
        onClose();
        toast({
          color: "primary",
          message:
            "Vielen Dank! Der Bericht wurde erfolgreich gesendet und jemand aus unserem Team wird sich drum kümmern.",
        });
      }}
    >
      {({ submit }) => (
        <Dialog fullWidth open={open} onClose={onClose}>
          <DialogTitle>{`Etwas ist schief gelaufen (${uid})`}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Unser Team wurde hierüber benachrichtigt. Wenn Sie uns helfen
              möchten, teilen Sie uns bitte unten mit, was geschehen ist.
            </DialogContentText>
            <TextInputField
              elementName="name"
              fullWidth
              label="Name"
              marginOverride={margin(1, 0)}
              placeholder="Jane Bloggs"
            />
            <TextInputField
              elementName="email"
              fullWidth
              label="E-mail"
              marginOverride={margin(1, 0)}
              placeholder="erika@beispiel.com"
            />
            <TextAreaInputField
              elementName="message"
              fullWidth
              label="Was ist passiert?"
              placeholder={`Ich habe auf das "X" und anschließend auf "Bestätigen" geklickt.`}
              rows={4}
            />
          </DialogContent>
          <DialogActions>
            <RSButton
              color="primary"
              id="close"
              loading="na"
              variant="text"
              onClick={onClose}
            >
              Schließen
            </RSButton>
            <RSButton
              color="primary"
              id="submit"
              inverted
              loading="na"
              variant="text"
              onClick={submit}
            >
              Absturzbericht senden
            </RSButton>
          </DialogActions>
        </Dialog>
      )}
    </SimpleFormRenderProps>
  );
}

export const ErrorPage = ({
  contactRecare,
  errorCode,
  sendBugReport,
  title,
  tryAgain,
  uid,
  web,
}: {
  contactRecare: string;
  errorCode: string;
  sendBugReport: string;
  title: string;
  tryAgain: string;
  uid: string;
  web: string;
}) => {
  const { isMobile } = useMedia();
  const [open, setOpen] = useModal();

  return (
    <div className="relative flex h-screen w-screen flex-1 flex-col items-center justify-center overflow-auto">
      <Image
        alt=""
        path="illustration-error-page.svg"
        style={{
          height: dp(157),
          marginBottom: isMobile ? sizing(1) : sizing(10),
          width: dp(295),
        }}
      />
      <VerticalLayout aligned>
        <Heading>{title}</Heading>
        <Title disabled>
          {errorCode}
          {uid}
        </Title>
      </VerticalLayout>
      <VerticalLayout aligned margin={margin(1, 0)}>
        <RSButton
          color="primary"
          id="try_again"
          loading="na"
          style={{ margin: margin(3, 0), width: dp(343) }}
          variant="contained"
          onClick={() => {
            window.localStorage.clear();
            setTimeout(() => window.location.reload(), 0);
          }}
        >
          {tryAgain}
        </RSButton>
        <Link href={web} rel="noopener noreferrer" target="_blank">
          {contactRecare}
        </Link>
      </VerticalLayout>
      <Link
        color={TEXT_DARK_HINT}
        rel="noopener noreferrer"
        style={{
          bottom: isMobile ? sizing(2) : sizing(5),
          position: "absolute",
        }}
        target="_blank"
        onClick={() => setOpen(true)}
      >
        {sendBugReport}
      </Link>
      <BugReportDialog open={open} setOpen={setOpen} uid={uid} />
    </div>
  );
};

function logError({
  componentStack,
  err,
  trackEvent,
  uid,
}: {
  componentStack: string;
  err: Error;
  trackEvent: TrackEventFn;
  uid: string;
}) {
  try {
    const errorTitle = `[Error boundary][${uid}]:`;

    const errorContent = `${err.name} ${err.message} ${componentStack}`;

    const [isEventFiltered, error] = shouldFilterEvent(errorContent);

    console.error(`${errorTitle} ${errorContent}`);

    try {
      let componentErrorSource = componentStack;
      componentErrorSource = componentErrorSource || "componentErrorSource";
      componentErrorSource = componentErrorSource.substring(
        componentErrorSource.indexOf("in") + 3,
      );
      componentErrorSource = componentErrorSource.substring(
        0,
        componentErrorSource.indexOf(" "),
      );
      if (
        typeof componentErrorSource === "string" &&
        componentErrorSource.length > 0
      ) {
        trackEvent({
          component: componentErrorSource,
          name: TRACK_EVENTS.COMPONENT_ERROR_HEATMAP,
          path: window.location.pathname,
        });
      }
      // eslint-disable-next-line no-empty
    } catch (_err) {}

    if (isEventFiltered) {
      trackEvent({
        error,
        name: TRACK_EVENTS.ERROR_BOUNDARY_FILTERED_EVENT,
      });
    } else {
      console.error(`${errorTitle} ${errorContent}`);
    }
  } catch (e) {
    console.error("[Error boundary exception]:", e, JSON.stringify(err));
  }
}

export function getTranslations() {
  return {
    contactRecare: "Kontaktieren Sie Recare",
    errorCode: "Fehlercode:",
    sendBugReport: "Fehlerbericht senden",
    title: "Etwas ist schief gelaufen",
    tryAgain: "Erneut versuchen",
    web: "https://recaresolutions.com/kontakt/",
  };
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode; trackEvent: TrackEventFn },
  {
    error: Error | null | undefined;
    info: ErrorInfo | null | undefined;
    uid: string;
  }
> {
  state = {
    error: null,
    info: null,
    uid: "",
  };
  componentDidCatch(error: Error, info: ErrorInfo): void {
    const uid = generateUID();
    logError({
      componentStack: info ? info.componentStack : "",
      err: error,
      trackEvent: this.props.trackEvent,
      uid,
    });
    this.setState({ error, info, uid });
  }

  render() {
    if (this.state.error) {
      const { contactRecare, errorCode, sendBugReport, title, tryAgain, web } =
        getTranslations();

      return (
        <ErrorPage
          contactRecare={contactRecare}
          errorCode={errorCode}
          sendBugReport={sendBugReport}
          title={title}
          tryAgain={tryAgain}
          uid={this.state.uid}
          web={web}
        />
      );
    }
    return this.props.children;
  }
}

export default withTrackingEvent(ErrorBoundary);
